import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Leaf, LogOut, LayoutDashboard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { HeroSection } from '@/components/landing/HeroSection';
import { FeaturesSection } from '@/components/landing/FeaturesSection';
import { Footer } from '@/components/landing/Footer';
import { RequestAccessModal } from '@/components/landing/RequestAccessModal';
import { useAuthStore } from '@/stores/authStore';
import { supabase } from '@/lib/supabase';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

export default function Landing() {
  const { user, loading } = useAuthStore();
  const [requestOpen, setRequestOpen] = useState(false);

  if (loading) return null;

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      toast.success('Signed out');
    } catch {
      toast.error('Failed to sign out');
    }
  };

  return (
    <div className="min-h-screen bg-background overflow-hidden">
      {/* Nav */}
      <motion.nav
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="fixed left-0 right-0 top-0 z-50 border-b border-border/20 bg-background"
      >
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2.5 font-display font-bold text-foreground">
            <div className="flex h-8 w-8 items-center justify-center rounded-xl gradient-primary shadow-sm">
              <Leaf className="h-4 w-4 text-white" />
            </div>
            <span className="tracking-tight">Velora</span>
          </div>

          {user ? (
            /* Logged-in nav */
            <div className="flex items-center gap-2">
              <Button size="sm" asChild className="rounded-xl gradient-primary border-0 gap-1.5 btn-glow transition-all duration-300 shadow-md shadow-primary/20">
                <Link to="/dashboard">
                  <LayoutDashboard className="h-3.5 w-3.5" />
                  Dashboard
                </Link>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSignOut}
                className="rounded-xl font-medium text-muted-foreground hover:text-destructive gap-1.5"
              >
                <LogOut className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          ) : (
            /* Logged-out nav */
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setRequestOpen(true)}
                className="rounded-xl font-medium text-muted-foreground hover:text-foreground hidden sm:flex"
              >
                Request Access
              </Button>
              <Button variant="ghost" size="sm" asChild className="rounded-xl font-medium">
                <Link to="/login">Sign In</Link>
              </Button>
              <Button
                size="sm"
                asChild
                className="rounded-xl gradient-primary border-0 btn-glow transition-all duration-300 shadow-md shadow-primary/20 hover:shadow-lg hover:shadow-primary/30"
              >
                <Link to="/signup">Get Started</Link>
              </Button>
            </div>
          )}
        </div>
      </motion.nav>

      <main>
        <HeroSection onRequestAccess={user ? undefined : () => setRequestOpen(true)} />
        <FeaturesSection />
      </main>
      <Footer />

      <RequestAccessModal open={requestOpen} onOpenChange={setRequestOpen} />
    </div>
  );
}
